import { useQuery } from '@tanstack/react-query';
import { ticketService } from '@/services/ticketService';
import { useProjectStore } from '@/store/projectStore';
import { Loader } from '@/components/common/Loader';
import { Badge } from '@/components/common/Badge';
import {
  ChartBarIcon,
  TicketIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// Color classes mapping for Tailwind static analysis
const colorClasses = {
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
  },
  yellow: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-600',
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-600',
  },
} as const;

type ColorType = keyof typeof colorClasses;

export function DashboardPage() {
  const { currentProject, clearProject } = useProjectStore();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['ticketStats', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return null;
      try {
        return await ticketService.getTicketStats(currentProject.id);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          // Stale project ID from another environment — clear it
          clearProject();
          return null;
        }
        throw err;
      }
    },
    enabled: !!currentProject,
    retry: false,
  });

  const cards = [
    {
      title: 'Total Tickets',
      value: stats?.total_tickets || 0,
      icon: TicketIcon,
      color: 'blue' as ColorType,
    },
    {
      title: 'Open Tickets',
      value: stats?.open_tickets || 0,
      icon: ClockIcon,
      color: 'yellow' as ColorType,
    },
    {
      title: 'In Progress',
      value: stats?.by_status?.in_progress || 0,
      icon: ChartBarIcon,
      color: 'purple' as ColorType,
    },
    {
      title: 'Resolved',
      value: stats?.resolved_tickets || 0,
      icon: CheckCircleIcon,
      color: 'green' as ColorType,
    },
  ];

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-gray-500 mb-4">No project selected</p>
          <p className="text-sm text-gray-400">Please select a project to view the dashboard</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          {currentProject && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
              {currentProject.name}
            </span>
          )}
        </div>
        <p className="mt-2 text-gray-600">Overview of your tickets and activity</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          const colors = colorClasses[card.color];
          return (
            <div
              key={card.title}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${colors.bg}`}>
                  <Icon className={`h-6 w-6 ${colors.text}`} />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {stats && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">By Status</h2>
            <div className="space-y-3">
              {Object.entries(stats.by_status || stats.tickets_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">{status.replace(/_/g, ' ')}</span>
                  <Badge variant="secondary">{count as number}</Badge>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">By Priority</h2>
            <div className="space-y-3">
              {Object.entries(stats.by_priority || stats.tickets_by_priority || {}).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">{priority}</span>
                  <Badge variant="secondary">{count as number}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
